/**
 * Copy all foods from a source meal option into a new option on another user's target plan
 * (same meal type, e.g. Breakfast -> Breakfast). Uses two API calls: create option, then batch-add foods.
 */
import { normalizeKey } from "../utils/normalizeDietKey";
import {
  fetchTargetPlanDetails,
  fetchTargetPlanSummaries,
  modifyTargetPlanMealOptions,
  modifyTargetPlanMealOptionFoods,
} from '../api/dietApi';
import { formatIntakeTimeForApi } from './intakeTime';

const matchQuantityTypeId = (unitKey, quantityTypes = []) => {
  if (!unitKey || !quantityTypes?.length) return null;
  const keyNorm = String(unitKey).trim().toLowerCase();
  const hit = quantityTypes.find((qt) => {
    const d = (qt?.description ?? "").trim().toLowerCase();
    const n = (qt?.name ?? "").trim().toLowerCase();
    return d === keyNorm || n === keyNorm;
  });
  return hit?.id || null;
};

function extractNewMealOptionId(modifyRes) {
  const api = modifyRes?.data?.data;
  const inner = api?.data;
  const list = inner?.meal_options;
  const first = Array.isArray(list) ? list[0] : null;
  return first?.id ?? null;
}

/**
 * @param {Object} params
 * @param {string} params.eventTypeId
 * @param {string} params.targetClientId - UUID of user the plan is assigned to (assigned_to_id)
 * @param {string} params.targetPlanId
 * @param {string} params.periodName - e.g. "Breakfast" (must match meal type on target plan)
 * @param {Array} params.sourceFoods - rows from savedFoodItems (food_id, quantity, quantity_type, intake_time, …)
 * @param {Object} params.dietCommonUtils
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function copyMealOptionToClientPlan({
  eventTypeId,
  targetClientId,
  targetPlanId,
  periodName,
  sourceFoods,
  dietCommonUtils,
}) {
  const foods = (sourceFoods || []).filter((f) => f?.food_id);
  if (!foods.length) {
    return { ok: false, message: "No foods to copy in this option." };
  }
  if (!eventTypeId || !targetClientId || !targetPlanId || !periodName) {
    return { ok: false, message: "Missing plan or user information." };
  }

  const detailsRes = await fetchTargetPlanDetails(
    eventTypeId,
    targetPlanId,
    targetClientId
  );
  if (!detailsRes.success) {
    return {
      ok: false,
      message:
        detailsRes.message ||
        "Could not load the target plan. Check user ID and plan access.",
    };
  }

  const planData = detailsRes.data?.data ?? detailsRes.data;
  const meals = planData?.meals || [];
  const periodKey = normalizeKey(periodName);
  const meal = meals.find(
    (m) =>
      normalizeKey(m.meal_type_description || m.meal_type || "") === periodKey
  );
  if (!meal?.id) {
    return {
      ok: false,
      message: `The target plan has no "${periodName}" meal. Add that meal type to the plan first.`,
    };
  }

  const options = meal.meal_options || [];
  const nums = options
    .map((o) => parseInt(String(o.title || "").match(/\d+/)?.[0], 10))
    .filter((n) => !Number.isNaN(n));
  const nextNum = nums.length ? Math.max(...nums) + 1 : 1;
  const title = `Option ${nextNum}`;

  const optRes = await modifyTargetPlanMealOptions({
    event_type_id: eventTypeId,
    meal_options: [
      {
        operation: "ADD",
        target_plan_meal_id: meal.id,
        title,
        description: "",
      },
    ],
  });

  if (!optRes.success) {
    return {
      ok: false,
      message: optRes.message || "Failed to create meal option on target plan.",
    };
  }

  const newOptionId = extractNewMealOptionId(optRes);
  if (!newOptionId) {
    return {
      ok: false,
      message: "Created option but could not read new option id from response.",
    };
  }

  const mealOptionFoods = [];
  for (const f of foods) {
    const recipeId = f.food_id;
    const unit =
      f.quantity_type ||
      f.quantity_type_description ||
      (dietCommonUtils?.quantity_types || []).find(
        (q) => q.id === f.quantity_type_id
      )?.description ||
      "g";
    const quantity_type_id = matchQuantityTypeId(
      unit,
      dietCommonUtils?.quantity_types
    );
    if (!quantity_type_id) {
      return {
        ok: false,
        message: `Could not map unit "${unit}" to a quantity type. Check diet settings.`,
      };
    }
    mealOptionFoods.push({
      operation: "ADD",
      target_plan_meal_option_id: newOptionId,
      food_id: recipeId,
      quantity: Number(f.quantity ?? 1),
      quantity_type_id,
      intake_time: formatIntakeTimeForApi(f.intake_time || f.time || ""),
    });
  }

  const foodRes = await modifyTargetPlanMealOptionFoods({
    event_type_id: eventTypeId,
    meal_option_foods: mealOptionFoods,
  });

  if (!foodRes.success) {
    return {
      ok: false,
      message:
        foodRes.message ||
        "Option was created but copying foods failed. You may need to add foods manually.",
    };
  }

  return {
    ok: true,
    message: `Copied ${foods.length} food(s) to ${title} on the selected plan.`,
  };
}

/**
 * List target plans for a client (assignee). Uses summaries API with client_id.
 */
export async function fetchDietPlansForClient(eventTypeId, clientId) {
  const res = await fetchTargetPlanSummaries(eventTypeId, null, null, clientId);
  if (!res.success) {
    return { ok: false, plans: [], message: res.message };
  }
  const raw = res.data?.data?.target_plans ?? res.data?.target_plans ?? [];
  const plans = Array.isArray(raw) ? raw : [];
  return { ok: true, plans, message: null };
}

export { matchQuantityTypeId, formatIntakeTimeForApi as formatIntakeTime };
